import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOptimizedPublisherAnalysis } from './context/OptimizedPublisherAnalysisContext';
import axios from 'axios';
import './style/publisherHeader.scss';
import { api } from '../../../src/utils/variables';

const PublisherHeader = () => {
  const { publisher, loader, categoryApps } = useOptimizedPublisherAnalysis();
  const navigate = useNavigate();
  const location = useLocation();
  const [socialMedia, setSocialMedia] = useState({});
  const [socialLoader, setSocialLoader] = useState(false);

  // Helper function to get platform metadata (icon, color, display name)
  const getPlatformMetadata = (platformName) => {
    const platform = platformName.toLowerCase();
    const metadata = {
      youtube: { icon: "fab fa-youtube", color: "#FF0000", displayName: "YouTube" },
      x: { icon: "fab fa-twitter", color: "#1DA1F2", displayName: "X" },
      twitter: { icon: "fab fa-twitter", color: "#1DA1F2", displayName: "X" },
      instagram: { icon: "fab fa-instagram", color: "#E4405F", displayName: "Instagram" },
      facebook: { icon: "fab fa-facebook", color: "#1877F2", displayName: "Facebook" },
      linkedin: { icon: "fab fa-linkedin", color: "#0077B5", displayName: "LinkedIn" },
      tiktok: { icon: "fab fa-tiktok", color: "#000000", displayName: "TikTok" },
      snapchat: { icon: "fab fa-snapchat", color: "#FFFC00", displayName: "Snapchat" },
      discord: { icon: "fab fa-discord", color: "#5865F2", displayName: "Discord" },
      reddit: { icon: "fab fa-reddit", color: "#FF4500", displayName: "Reddit" },
      twitch: { icon: "fab fa-twitch", color: "#9146FF", displayName: "Twitch" }
    };
    return metadata[platform] || { icon: "fab fa-globe", color: "#6c757d", displayName: platformName };
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
    return num.toString();
  };

  // "Go Back" button handler similar to GameDashboard
  const handleGoBack = () => {
    // Prefer explicit "from" in navigation state, otherwise history, then fallback to dashboard
    try {
      const from = location && location.state && location.state.from;
      if (from) {
        navigate(from);
        return;
      }
    } catch (err) {
      // ignore
    }

    if (window && window.history && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  // Fetch social media data when publisher name is available
  useEffect(() => {
    // Only fetch if we have the actual publisher name from context, not fallback text
    const publisherName = publisher.name;

    if (publisherName && publisherName !== 'Unknown Publisher' && !loader) {
      // Only fetch for real publisher names, not fallback like "Publisher of X apps"
      if (!publisherName.startsWith('Publisher of')) {
        fetchSocialMediaData(publisherName);
      }
    }
  }, [publisher.name, loader]);

  const fetchSocialMediaData = async (publisherName) => {
    try {
      setSocialLoader(true);
      const socialsRes = await axios.get(
        `${api}/tracker/publisher/socials?publisher_name=${encodeURIComponent(publisherName)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (socialsRes.data?.data && Array.isArray(socialsRes.data.data)) {
        const socials = {};
        socialsRes.data.data.forEach((platformData) => {
          // Use platform name directly from API (e.g., "X", "YouTube", "Instagram")
          const platformName = platformData.platform;
          if (!platformName) return;

          const platformNameLower = platformName.toLowerCase();
          const normalizedKey = platformNameLower === "x" || platformNameLower === "twitter" ? "x" : platformNameLower;

          // Use link first (from accounts), then profile_url, then construct from handle
          let profileUrl = platformData.link || platformData.profile_url;

          // If still no URL, try to get from accounts array if available
          if (!profileUrl && platformData.accounts && Array.isArray(platformData.accounts) && platformData.accounts.length > 0) {
            // Use the first account's link
            profileUrl = platformData.accounts[0].link || null;
          }

          // Fallback: construct URL from handle if still no URL
          if (!profileUrl && platformData.handle) {
            const handle = platformData.handle.replace('@', '');
            const urlMap = {
              youtube: `https://youtube.com/@${handle}`,
              x: `https://x.com/${handle}`,
              twitter: `https://x.com/${handle}`,
              instagram: `https://instagram.com/${handle}`,
              facebook: `https://facebook.com/${handle}`,
              linkedin: `https://linkedin.com/in/${handle}`,
              tiktok: `https://tiktok.com/@${handle}`,
              snapchat: `https://snapchat.com/add/${handle}`,
              discord: `https://discord.gg/${handle}`,
              reddit: `https://reddit.com/user/${handle}`,
              twitch: `https://twitch.tv/${handle}`,
            };
            profileUrl = urlMap[normalizedKey] || null;
          }

          // Only add if we have a valid URL (all icons should be clickable)
          if (profileUrl) {
            socials[normalizedKey] = {
              handle: platformData.handle,
              url: profileUrl,
              followers: platformData.followers || 0,
              followersDisplay: platformData.followers_display || null,
              platform: platformName, // Use platform name directly from API
            };
          }
        });
        setSocialMedia(socials);
        console.log('Social media data loaded:', socials);
      }
    } catch (err) {
      console.error("Error fetching publisher social media:", err);
    } finally {
      setSocialLoader(false);
    }
  };

  if (loader) {
    return (
      <div className="publisher-header">
        <div className="publisher-logo">
          <div className="logo-circle">
            <div className="logo-inner">
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </div>
          </div>
          <h1 className="publisher-name">
            <div className="placeholder-glow">
              <span className="placeholder col-6"></span>
            </div>
          </h1>
        </div>
        <div className="publisher-actions">
          <button className="btn btn-search-more" disabled title="Loading...">Search More Publishers</button>
          <button className="btn btn-track" disabled title="Publisher tracking feature coming soon.">Track (Coming Soon)</button>
        </div>
      </div>
    );
  }

  // Get publisher name from context or from the first app's data
  const publisherName = publisher.name ||
    (categoryApps && categoryApps.length > 0 ?
      `Publisher of ${categoryApps.length} app${categoryApps.length !== 1 ? 's' : ''}` :
      'Unknown Publisher'
    );

  // Extract first letters for the logo and limit to reasonable length
  const words = publisherName.split(' ');
  const logoWords = words.slice(0, 2); // Only use first 2 words for logo

  // Truncate very long publisher names for display
  const displayName = publisherName.length > 50 ?
    publisherName.substring(0, 47) + '...' :
    publisherName;

  // Get publisher name from context for navigation
  const handleSocialClick = () => {
    if (publisherName && publisherName !== 'Unknown Publisher') {
      navigate(`/publisher-socials?publisher_name=${encodeURIComponent(publisherName)}`);
    } else {
      // Fallback to item_id if publisher name is not available
      const queryParams = new URLSearchParams(location.search);
      const itemId = queryParams.get("item_id");
      if (itemId) {
        navigate(`/publisher-socials?item_id=${itemId}`);
      } else {
        navigate('/publisher-socials');
      }
    }
  };

  return (
    <div className="publisher-header">
      {/* Top row: Go Back on left, actions on right */}
      <div className="publisher-header-top">
        <button
          type="button"
          onClick={handleGoBack}
          className="publisher-go-back-btn"
          style={{
            display: "inline-block",
            background: "#2a2b37",
            color: "#f43f5e",
            padding: "5px 10px",
            borderRadius: "6px",
            fontWeight: 600,
            textDecoration: "none",
            transition: "0.2s",
            whiteSpace: "nowrap",
            border: "1px solid #3a3b47",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#3a3b47";
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#2a2b37";
            e.currentTarget.style.color = "#f43f5e";
          }}
        >
          &larr; Go Back
        </button>
        <div className="publisher-actions">
          {/* Navigate back to Publisher Explorer */}
          <button
            className="btn btn-search-more"
            onClick={() => navigate('/publisher-analysis')}
            aria-label="Search more publishers"
          >
            Search More Publishers...
          </button>

          {/* Navigate to Publisher Social Signals */}
          <button
            className="btn btn-social"
            onClick={handleSocialClick}
            aria-label="View publisher socials"
          >
            See more Social Signals...
          </button>

          {/* Disabled track button with tooltip wrapper (so tooltip works while button is disabled) */}
          <div className="track-tooltip" data-tooltip="Publisher tracking feature coming soon.">
            <button
              className="btn btn-track"
              disabled
              aria-disabled="true"
            >
              Track (Coming Soon)
            </button>
          </div>
        </div>
      </div>

      {/* Second row: publisher logo + name + socials */}
      <div className="publisher-logo">
        <div className="logo-circle">
          <div className="logo-inner">
            <div className="logo-text">
              {logoWords.map((word, index) => (
                <div key={index} className="logo-word">
                  {word.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <h1 className="publisher-name">{displayName}</h1>
          {/* Social Media Icons */}
          {!socialLoader && Object.keys(socialMedia).length > 0 && (
            <div className="publisher-social-icons" style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginTop: "12px",
              flexWrap: "wrap"
            }}>
              {Object.entries(socialMedia).map(([platformKey, platformData]) => {
                if (!platformData || !platformData.url) return null;
                // Use platform name directly from API, fallback to platformKey for metadata lookup
                const platformNameFromAPI = platformData.platform || platformKey;
                const metadata = getPlatformMetadata(platformNameFromAPI);
                const displayFollowers = platformData.followersDisplay || formatNumber(platformData.followers || 0);

                // All icons are clickable links
                return (
                  <a
                    key={platformKey}
                    href={platformData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Visit ${metadata.displayName} profile`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      textDecoration: "none",
                      color: "#b0b0b5",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      cursor: "pointer",
                      padding: "4px 8px",
                      borderRadius: "6px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = metadata.color;
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#b0b0b5";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <i className={metadata.icon} style={{ fontSize: "18px", color: metadata.color, pointerEvents: "none" }}></i>
                    <span style={{ pointerEvents: "none" }}>{displayFollowers}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublisherHeader;
